import { useAppDispatch, useAppSelector } from 'appState/store'
import { LoadingBox } from '@outbound-ai/ursa'
import { RefreshIntervalsEnum } from 'configs/apiConfigs'
import { startCall } from 'features/calls/callsApi'
import { useCall } from 'features/calls/callsHooks'
import { callsSliceLiveCallInitialState, updateLiveCall } from 'features/calls/callsSlice'
import { AutomationTypeEnum, CallStatusEnum, CallTypeEnum, ICall, UseCaseEnum } from 'features/calls/types'
import { useClaimCalls, useDiagnosticClaim } from 'features/claims/claimsHooks'
import { ClaimDetailsCalls } from 'features/claims/components/ClaimDetails/ClaimDetailsCalls'
import { useEffect, useState } from 'react'
import { FaPhoneAlt } from 'react-icons/fa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { handleApiRequestWithStates } from 'utils'
import { errorHandler } from 'utils/errors/errors'
import { TestCallChatModal } from './TestCallChatModal'
import { ViewCallTranscriptModal } from './ViewCallTranscriptModal'
import { Button } from '@outbound-ai/ursa'

export const SettingsDiagnosticCall = () => {
  const dispatch = useAppDispatch()

  const { isConnected } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const [showTestCallModal, setShowTestCallModal] = useState(false)
  const [showCallTranscript, setShowCallTranscript] = useState(false)
  const [testCall, setTestCall] = useState<ICall | null>(null)
  const [isLoadingStartCall, setIsLoadingStartCall] = useState(false)
  const [viewedCallIndex, setViewedCallIndex] = useState(0)

  const { data: claim, isLoading } = useDiagnosticClaim()
  const { data: unsortedCalls, mutate: mutateCalls } = useClaimCalls(claim?.oaiClaimId)
  const { data: ongoingCall } = useCall(testCall?.jobId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })
  const calls: ICall[] = unsortedCalls?.sort((a, b) => ('' + a.jobRequested).localeCompare(b.jobRequested)) || []

  useEffect(() => {
    if (
      ongoingCall &&
      [
        CallStatusEnum.Complete,
        CallStatusEnum.FailedToComplete,
        CallStatusEnum.FailedToQueue,
        CallStatusEnum.Unknown,
      ].includes(ongoingCall.status as CallStatusEnum)
    ) {
      dispatch(
        updateLiveCall({
          ...callsSliceLiveCallInitialState,
          activeCallJobId: '',
          lastCallJobIdUserCompleted: testCall?.jobId,
        }),
      )
      setTestCall(null)
      mutateCalls()
      window.location.reload()
    }
  }, [ongoingCall])

  useEffect(() => {
    if (!isConnected) {
      setShowTestCallModal(false)
    }
  }, [isConnected])

  const handleStartCall = async () => {
    if (!claim) return

    const { data: jobResponse, error } = await handleApiRequestWithStates(
      () => startCall(claim.oaiClaimId, { type: CallTypeEnum.diagnosticHeadset, useCase: UseCaseEnum.CSI }),
      setIsLoadingStartCall,
    )

    if (jobResponse) {
      dispatch(
        updateLiveCall({
          activeCallJobId: jobResponse.jobId,
          type: CallTypeEnum.diagnosticHeadset,
          activeClaimId: claim?.oaiClaimId,
          automationType: AutomationTypeEnum.basic,
        }),
      )
      setTestCall(jobResponse)
      setShowTestCallModal(true)
    }

    if (error) errorHandler.handleError(error, ErrorMessagesEnum.StartCall)
  }

  return (
    <>
      {isLoading && <LoadingBox />}
      {!isLoading && (
        <Div className="p-relative my-1 d-flex align-items-start gap-4 justify-content-evenly">
          {claim && (
            <>
              <div className="d-flex flex-row justify-content-center align-items-center">
                <section className="diagnostic-call-container">
                  <span className="phone-icon">
                    <FaPhoneAlt size={48} />
                  </span>
                  <p>Conduct a test call to verify your audio hardware is working properly.</p>
                  <Button
                    variant="info"
                    radius="rounded"
                    size="normal"
                    width="100%"
                    disabled={isConnected}
                    onClick={handleStartCall}
                  >
                    {'Begin Test Call'}
                  </Button>
                  <TestCallChatModal
                    claim={claim}
                    call={testCall}
                    show={showTestCallModal}
                    setShow={setShowTestCallModal}
                    isLoadingStartCall={isLoadingStartCall}
                  />
                </section>
                <ClaimDetailsCalls
                  onViewTranscript={setShowCallTranscript}
                  isDiagnosticScreen={true}
                  claim={claim}
                  onChangeCallIndex={setViewedCallIndex}
                />
              </div>

              {showCallTranscript && calls?.[viewedCallIndex] && (
                <ViewCallTranscriptModal
                  show={showCallTranscript}
                  setShow={setShowCallTranscript}
                  call={calls?.[viewedCallIndex]}
                  claim={claim}
                  callIndex={viewedCallIndex}
                  totalCalls={calls.length}
                />
              )}
            </>
          )}
        </Div>
      )}
    </>
  )
}

const Div = styled.div`
  max-width: ${({ theme }) => theme.util.pixelToRem(708)};
  border: 1px solid ${({ theme }) => theme.colors.primary.lightest};
  border-radius: ${({ theme }) => theme.borderRadius.rounded};
  padding: 2rem;
  .diagnostic-call-container {
    max-width: ${({ theme }) => theme.util.pixelToRem(288)};
    margin-right: ${({ theme }) => theme.util.pixelToRem(46)};
    display: flex;
    flex-direction: column;
    align-items: center;
    span.phone-icon {
      color: ${({ theme }) => theme.colors.secondary.dark};
    }
    p {
      font-size: 1rem;
      color: ${({ theme }) => theme.colors.grey.darker};
      margin-top: ${({ theme }) => theme.util.pixelToRem(35)};
      margin-bottom: 1.5rem;
    }
    div:not(.spinner-grow) {
      width: 100%;
    }
  }
  .claim-details-calls-body {
    height: auto;
    padding-bottom: 1rem;
  }
`
